import {Button, ButtonProps, Stack, Text, TextInput} from '@mantine/core';
import {modals} from '@mantine/modals';
import {AiOutlineStop} from 'react-icons/ai';

import {useCancelExecutionMutation} from '../hooks/useExecutionsQuery.ts';
import {Execution, ExecutionStatus} from '../types.ts';

const CANCELLABLE_STATUSES: ExecutionStatus[] = [
    ExecutionStatus.PENDING,
    ExecutionStatus.RUNNING,
];

interface CancelExecutionButtonProps extends ButtonProps {
    execution: Execution;
}

export const CancelExecutionButton = ({
                                          execution,
                                          ...buttonProps
                                      }: CancelExecutionButtonProps) => {

    const cancelMutation = useCancelExecutionMutation();

    const disabled = !execution.executionStatus
        || !CANCELLABLE_STATUSES.includes(execution.executionStatus);

    const handleCancel = (reason: string) => {
        cancelMutation.mutate({
            id: execution.id,
            reason: reason.trim() ? reason.trim() : undefined,
        });
    };

    // The reason is captured in the SAME step as the confirmation (FNP-984).
    // It is held in a closure rather than React state because the modal's
    // children are rendered once by @mantine/modals and are not re-rendered by
    // this component — onConfirm reads whatever the field last held.
    const openConfirmModal = () => {

        let reason = '';

        const label = execution.name ?? execution.id;

        modals.openConfirmModal({
                                    title: 'Cancel execution',
                                    children: (
                                        <Stack gap='sm'>
                                            <Text size='sm'>
                                                Any in-flight Step Functions tasks for
                                                &quot;{label}&quot; will be stopped. Are you sure you want to
                                                cancel this execution?
                                            </Text>
                                            <TextInput
                                                label='Reason for cancellation'
                                                description='Optional'
                                                placeholder='Why is this being cancelled?'
                                                onChange={(event) => {
                                                    reason = event.currentTarget.value;
                                                }}
                                            />
                                        </Stack>
                                    ),
                                    labels: {
                                        confirm: 'Cancel execution',
                                        cancel: 'Keep running'
                                    },
                                    confirmProps: {color: 'red'},
                                    onConfirm: () => {
                                        handleCancel(reason);
                                    },
                                });
    };

    return (
        <Button
            color={'red'}
            variant={'outline'}
            disabled={disabled}
            loading={cancelMutation.isPending}
            leftSection={<AiOutlineStop/>}
            onClick={openConfirmModal}
            {...buttonProps}
        >
            Cancel Execution
        </Button>
    );
};
